/**
 * PDF Parser Module Exports
 */

// Types
export type {
  VisaDocumentType,
  DocumentDetectionResult,
  PDFExtractionResult,
  PageContent,
  DS160Data,
  I129Data,
  LCAData,
  EmploymentEntry,
  EducationEntry,
  SocialMediaEntry,
  ParsedVisaDocument,
  OCRStatus,
} from './types';

// PDF Extraction
export {
  extractTextFromPDF,
  isPDFScanned,
  checkWebGPUSupport,
  loadSmolDoclingModel,
  unloadSmolDoclingModel,
} from './pdfExtractor';

// Document Detection
export {
  detectDocumentType,
  getDocumentName,
  getConfidenceLevel,
} from './documentDetector';

// Parsers
export { parseDS160 } from './ds160Parser';
export { parseI129 } from './i129Parser';

// Convenience function to parse any visa document
import type { ParsedVisaDocument, PDFExtractionResult, OCRStatus } from './types';
import { extractTextFromPDF } from './pdfExtractor';
import { detectDocumentType } from './documentDetector';
import { parseDS160 } from './ds160Parser';
import { parseI129 } from './i129Parser';

export async function parseVisaDocument(
  file: File,
  onProgress?: (status: OCRStatus) => void
): Promise<{ extraction: PDFExtractionResult; parsed: ParsedVisaDocument }> {
  // Extract text from PDF
  const extraction = await extractTextFromPDF(file, onProgress);

  // Detect document type
  const detection = detectDocumentType(extraction.text);

  // Parse based on type
  let parsed: ParsedVisaDocument;

  switch (detection.type) {
    case 'DS-160':
      parsed = { type: 'DS-160', data: parseDS160(extraction.text) };
      break;
    case 'I-129':
      parsed = { type: 'I-129', data: parseI129(extraction.text) };
      break;
    case 'LCA':
      // LCA parser not implemented yet
      parsed = { type: 'unknown', data: null };
      break;
    default:
      parsed = { type: 'unknown', data: null };
  }

  return { extraction, parsed };
}
